/**
 * Tablero Kanban: una columna por estado abierto, en el orden del flujo de trabajo, y una sola
 * columna «Cerradas» con las hechas y las descartadas juntas (cada tarjeta lleva su marca).
 *
 * Es un componente de presentación: recibe las tareas y avisa qué se quiere hacer (abrir, mover),
 * pero no sabe de la API. Eso lo hace fácil de probar y de reutilizar.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ESTADOS_ABIERTOS,
  ETIQUETA_ESTADO,
  esEstadoCerrado,
  type Estado,
  type Tarea,
} from '@cdtec-backlog/contrato';
import { Esqueleto, TextoCargando } from '../../componentes/Esqueleto';
import type { FiltroEstado } from './api';
import { TarjetaTarea } from './TarjetaTarea';
import './Tablero.css';

interface Props {
  tareas: Tarea[];
  /** Filtro «Estado»: decide qué columnas se ven (ver `columnasVisibles`). */
  filtroEstado: FiltroEstado;
  puedeEscribir: boolean;
  /** Tareas movidas que esperan la confirmación del servidor (se ven atenuadas con un giro). */
  moviendo?: ReadonlySet<number>;
  onAbrir: (id: number) => void;
  onMover: (id: number, estado: Estado) => void;
  /**
   * Tarjeta a enfocar cuando llegue a `estado` (tras confirmar un cierre en un diálogo, el botón que la movió
   * ya no existe). Si su columna no se ve (p. ej. «Cerradas» con el filtro «Abiertas»), el foco no se mueve.
   */
  enfocar?: { id: number; estado: Estado } | null;
  onEnfocada?: () => void;
}

/** Clave de una columna: un estado abierto, o `CERRADAS` (hechas y descartadas). */
export type ColumnaTablero = Estado | 'CERRADAS';

/**
 * Columnas según el filtro «Estado»: «Abiertas» muestra solo las abiertas, «Todas» agrega la columna
 * «Cerradas» al final y los filtros de solo cerradas (Cerradas, Hechas, Descartadas) muestran solo esa
 * columna (las abiertas quedarían vacías: ruido).
 */
export function columnasVisibles(filtroEstado: FiltroEstado): ColumnaTablero[] {
  if (filtroEstado === 'abiertas') return [...ESTADOS_ABIERTOS];
  if (filtroEstado === 'todas') return [...ESTADOS_ABIERTOS, 'CERRADAS'];
  return ['CERRADAS'];
}

const tituloDe = (columna: ColumnaTablero) =>
  columna === 'CERRADAS' ? 'Cerradas' : ETIQUETA_ESTADO[columna];

const esDeLaColumna = (tarea: Tarea, columna: ColumnaTablero) =>
  columna === 'CERRADAS' ? esEstadoCerrado(tarea.estado) : tarea.estado === columna;

/** En «Cerradas», lo cerrado más recientemente arriba. */
const porCierre = (a: Tarea, b: Tarea) => (b.cerradaEn ?? '').localeCompare(a.cerradaEn ?? '');

export function Tablero({
  tareas,
  filtroEstado,
  puedeEscribir,
  moviendo,
  onAbrir,
  onMover,
  enfocar = null,
  onEnfocada,
}: Props) {
  const [columnaSobre, setColumnaSobre] = useState<ColumnaTablero | null>(null);
  const columnas = columnasVisibles(filtroEstado);
  const raiz = useRef<HTMLDivElement>(null);

  // Espera a que el movimiento (optimista) deje la tarjeta en su columna nueva y entonces la enfoca.
  useEffect(() => {
    if (!enfocar) return;
    const tarea = tareas.find((t) => t.id === enfocar.id);
    if (tarea && tarea.estado !== enfocar.estado) return;
    raiz.current
      ?.querySelector<HTMLElement>(`[data-tarea="${enfocar.id}"] .tarjeta__abrir`)
      ?.focus();
    onEnfocada?.();
  }, [enfocar, tareas, onEnfocada]);

  return (
    <div ref={raiz} className="tablero">
      {columnas.map((columna) => {
        const tarjetas = tareas.filter((tarea) => esDeLaColumna(tarea, columna));
        if (columna === 'CERRADAS') tarjetas.sort(porCierre);
        return (
          <section
            key={columna}
            className={[
              'columna',
              columnaSobre === columna && 'columna--sobre',
              columna === 'PARA_PRODUCCION' && 'columna--condicionada',
              columna === 'CERRADAS' && 'columna--cerradas',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={`${tituloDe(columna)}: ${tarjetas.length} tareas`}
            onDragOver={(evento) => {
              if (!puedeEscribir) return;
              evento.preventDefault();
              setColumnaSobre(columna);
            }}
            onDragLeave={() => setColumnaSobre(null)}
            onDrop={(evento) => {
              evento.preventDefault();
              setColumnaSobre(null);
              const id = Number(evento.dataTransfer.getData('text/plain'));
              const tarea = tareas.find((t) => t.id === id);
              if (!tarea || esDeLaColumna(tarea, columna)) return;
              // Soltar en «Cerradas» es terminarla: descartar pide un motivo y se hace desde la ficha.
              onMover(id, columna === 'CERRADAS' ? 'HECHO' : columna);
            }}
          >
            <h2 className="columna__titulo">
              <span>{tituloDe(columna)}</span>
              <span className="columna__cantidad">{tarjetas.length}</span>
            </h2>
            {tarjetas.length === 0 && <p className="columna__vacia">Nada aquí</p>}
            {tarjetas.map((tarea) => (
              <TarjetaTarea
                key={tarea.id}
                tarea={tarea}
                puedeEscribir={puedeEscribir}
                moviendo={moviendo?.has(tarea.id) ?? false}
                onAbrir={onAbrir}
                onMover={onMover}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

/** Tarjeta fantasma con la misma estructura que `TarjetaTarea`: tope, título de 1 o 2 líneas, chips y pie. */
function TarjetaFantasma({ dosLineas }: { dosLineas: boolean }) {
  return (
    <div className="tarjeta tarjeta--fantasma esq-caja" aria-hidden="true">
      <span className="tarjeta__tope">
        <Esqueleto forma="icono" />
        <Esqueleto ancho={30} />
      </span>
      <span className="tarjeta-fantasma__titulo">
        <Esqueleto forma="titulo" ancho={90} />
        {dosLineas && <Esqueleto forma="titulo" ancho={60} />}
      </span>
      <span className="etiquetas">
        <Esqueleto forma="chip" />
        <Esqueleto forma="chip" />
      </span>
      <span className="tarjeta__pie">
        <Esqueleto forma="avatar" />
        <Esqueleto ancho={40} />
      </span>
    </div>
  );
}

/**
 * Primera carga del tablero: las columnas con su título real (no depende del servidor) y 2 o 3
 * tarjetas fantasma del mismo alto que las reales. `mostrar` llega a los ~300 ms: antes, las tarjetas
 * ocupan su lugar pero invisibles (si los datos llegan rápido, no hay parpadeo).
 */
export function TableroCargando({
  filtroEstado,
  mostrar,
}: {
  filtroEstado: FiltroEstado;
  mostrar: boolean;
}) {
  // El aviso para lectores va fuera de la zona ocupada: dentro de `aria-busy` no se anunciaría.
  return (
    <>
      <TextoCargando>Cargando tareas…</TextoCargando>
      <div className={`tablero${mostrar ? '' : ' carga--espera'}`} aria-busy="true">
        {columnasVisibles(filtroEstado).map((columna, i) => (
          <section key={columna} className="columna" aria-label={tituloDe(columna)}>
            <h2 className="columna__titulo">
              <span>{tituloDe(columna)}</span>
            </h2>
            {Array.from({ length: i % 2 === 0 ? 3 : 2 }, (_, n) => (
              <TarjetaFantasma key={n} dosLineas={(n + i) % 2 === 1} />
            ))}
          </section>
        ))}
      </div>
    </>
  );
}
